import { useState, useEffect } from 'react';
import { reportesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { Printer, Search, X, ChevronDown, ChevronRight } from 'lucide-react';

const ESTADOS_OT = ['', 'EN EJECUCION', 'PENDIENTE', 'COMPLETADA', 'DETENIDA', 'CERRADA'];
const ESTADOS_PROYECTO = ['', 'VIGENTE', 'CERRADO'];

export const Reportes = () => {
  const [activeReport, setActiveReport] = useState('hh-ot');
  const [loading, setLoading] = useState(false);
  const [dataHHOT, setDataHHOT] = useState([]);
  const [dataHHProyecto, setDataHHProyecto] = useState([]);
  const [expandedProyectos, setExpandedProyectos] = useState({});
  const [proyectosLista, setProyectosLista] = useState([]);
  const [otsLista, setOtsLista] = useState([]);
  
  const [filtroEstadoOT, setFiltroEstadoOT] = useState('EN EJECUCION');
  const [filtroOTDesde, setFiltroOTDesde] = useState('');
  const [filtroOTHasta, setFiltroOTHasta] = useState('');
  const [filtroProyectoOT, setFiltroProyectoOT] = useState('');
  
  const [filtroEstadoProyecto, setFiltroEstadoProyecto] = useState('VIGENTE');
  const [filtroProyectoCodigo, setFiltroProyectoCodigo] = useState('');

  useEffect(() => {
    loadProyectosLista();
    loadOTsLista();
  }, []);

  useEffect(() => {
    if (activeReport === 'hh-ot') {
      loadReporteHHOT();
    } else if (activeReport === 'hh-proyecto') {
      loadReporteHHProyecto();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport]);

  const loadProyectosLista = async () => {
    try {
      const res = await reportesApi.getProyectosLista();
      setProyectosLista(res.data);
    } catch (error) { console.error('caught:', error); }
  };

  const loadOTsLista = async () => {
    try {
      const res = await reportesApi.getOTsLista();
      setOtsLista(res.data);
    } catch (error) { console.error('caught:', error); }
  };

  const loadReporteHHOT = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstadoOT) params.estado = filtroEstadoOT;
      if (filtroOTDesde) params.ot_desde = parseInt(filtroOTDesde);
      if (filtroOTHasta) params.ot_hasta = parseInt(filtroOTHasta);
      if (filtroProyectoOT) params.proyecto_codigo = filtroProyectoOT;
      
      const res = await reportesApi.getHHPorOT(params);
      setDataHHOT(res.data);
    } catch (error) {

      toast({ title: 'Error al cargar el reporte', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadReporteHHProyecto = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstadoProyecto) params.estado_proyecto = filtroEstadoProyecto;
      if (filtroProyectoCodigo) params.proyecto_codigo = filtroProyectoCodigo;
      
      const res = await reportesApi.getHHPorProyecto(params);
      setDataHHProyecto(res.data);
      
      if (res.data.length === 1) {
        setExpandedProyectos({ [res.data[0].proyecto_codigo]: true });
      }
    } catch (error) {

      toast({ title: 'Error al cargar el reporte', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarHHOT = () => loadReporteHHOT();
  const handleBuscarHHProyecto = () => loadReporteHHProyecto();
  const limpiarFiltrosOT = () => { setFiltroEstadoOT(''); setFiltroOTDesde(''); setFiltroOTHasta(''); setFiltroProyectoOT(''); };
  const limpiarFiltrosProyecto = () => { setFiltroEstadoProyecto(''); setFiltroProyectoCodigo(''); };
  const handlePrint = () => window.print();
  const toggleProyecto = (codigo) => setExpandedProyectos(prev => ({ ...prev, [codigo]: !prev[codigo] }));
  const fmt = (num) => num?.toLocaleString('es-CL', { maximumFractionDigits: 1 }) || '0';

  const getTotalesHHOT = () => dataHHOT.reduce((acc, ot) => ({
    presupuestadas: acc.presupuestadas + (ot.hh_presupuestadas || 0),
    reales: acc.reales + (ot.hh_reales || 0),
    normales: acc.normales + (ot.hh_normales || 0),
    extra: acc.extra + (ot.hh_extra || 0)
  }), { presupuestadas: 0, reales: 0, normales: 0, extra: 0 });

  const getTotalesProyecto = () => dataHHProyecto.reduce((acc, proy) => ({
    presupuestadas: acc.presupuestadas + (proy.total_hh_presupuestadas || 0),
    reales: acc.reales + (proy.total_hh_reales || 0),
    ots: acc.ots + (proy.cantidad_ots || 0)
  }), { presupuestadas: 0, reales: 0, ots: 0 });

  const selectStyle = "h-7 px-2 border border-gray-400 text-xs bg-white";
  const btnStyle = "h-7 px-2 text-xs bg-gray-800 text-white hover:bg-gray-700";

  return (
    <div className="p-2" data-testid="reportes-page">
      {/* Header compacto */}
      <div className="flex justify-between items-center mb-2 print:hidden">
        <h1 className="text-lg font-bold">REPORTES - HORAS HOMBRE</h1>
        <Button onClick={handlePrint} size="sm" variant="outline" className="h-7 text-xs print:hidden">
          <Printer className="w-3 h-3 mr-1" />Imprimir
        </Button>
      </div>

      {/* Tabs compactos */}
      <div className="flex gap-1 mb-2 print:hidden">
        <button onClick={() => setActiveReport('hh-ot')} className={`px-3 py-1 text-xs font-medium border ${activeReport === 'hh-ot' ? 'bg-gray-800 text-white' : 'bg-gray-100'}`}>
          HH por OT
        </button>
        <button onClick={() => setActiveReport('hh-proyecto')} className={`px-3 py-1 text-xs font-medium border ${activeReport === 'hh-proyecto' ? 'bg-gray-800 text-white' : 'bg-gray-100'}`}>
          HH por Proyecto
        </button>
      </div>

      {/* Reporte HH por OT */}
      {activeReport === 'hh-ot' && (
        <div>
          {/* Filtros en línea */}
          <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-100 border print:hidden">
            <span className="text-xs font-bold">Filtros:</span>
            <select value={filtroEstadoOT} onChange={(e) => setFiltroEstadoOT(e.target.value)} className={selectStyle}>
              <option value="">Estado: Todos</option>
              {ESTADOS_OT.filter(e => e).map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select value={filtroOTDesde} onChange={(e) => setFiltroOTDesde(e.target.value)} className={selectStyle}>
              <option value="">OT Desde</option>
              {otsLista.map(ot => <option key={ot.numero_ot} value={ot.numero_ot}>OT-{ot.numero_ot}</option>)}
            </select>
            <select value={filtroOTHasta} onChange={(e) => setFiltroOTHasta(e.target.value)} className={selectStyle}>
              <option value="">OT Hasta</option>
              {otsLista.map(ot => <option key={ot.numero_ot} value={ot.numero_ot}>OT-{ot.numero_ot}</option>)}
            </select>
            <select value={filtroProyectoOT} onChange={(e) => setFiltroProyectoOT(e.target.value)} className={selectStyle + " max-w-[180px]"}>
              <option value="">Proyecto: Todos</option>
              {proyectosLista.map(p => <option key={p.codigo} value={p.codigo}>{p.codigo}</option>)}
            </select>
            <Button onClick={handleBuscarHHOT} size="sm" className={btnStyle}><Search className="w-3 h-3 mr-1" />Buscar</Button>
            <Button onClick={limpiarFiltrosOT} size="sm" variant="outline" className="h-7 px-2 text-xs"><X className="w-3 h-3" /></Button>
            <span className="text-xs ml-auto">{dataHHOT.length} OTs</span>
          </div>

          {/* Título para impresión */}
          <div className="hidden print:block text-center mb-2">
            <h2 className="text-sm font-bold">INFORME HH POR ORDEN DE TRABAJO</h2>
            <p className="text-xs">Estado: {filtroEstadoOT || 'Todos'} | OT: {filtroOTDesde || '-'} a {filtroOTHasta || '-'}</p>
          </div>

          {/* Tabla compacta con bordes */}
          {loading ? (
            <p className="text-center py-4 text-xs">Cargando...</p>
          ) : dataHHOT.length === 0 ? (
            <p className="text-center py-4 text-xs text-gray-500">Sin datos</p>
          ) : (
            <table className="report-table bordered-table w-full">
              <thead>
                <tr>
                  <th>OT</th>
                  <th>DESCRIPCIÓN</th>
                  <th>CLIENTE</th>
                  <th>PROYECTO</th>
                  <th>ESTADO</th>
                  <th className="text-right">HH PRES.</th>
                  <th className="text-right">HH REAL</th>
                  <th className="text-right">DIF.</th>
                  <th className="text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {dataHHOT.map((ot, idx) => (
                  <tr key={idx}>
                    <td className="font-mono font-bold text-center">{ot.numero_ot}</td>
                    <td className="max-w-[150px] truncate">{ot.descripcion}</td>
                    <td className="max-w-[130px] truncate" data-testid={`hh-ot-cliente-${ot.numero_ot}`}>{ot.cliente_nombre || '-'}</td>
                    <td className="font-mono text-center">{ot.proyecto_codigo}</td>
                    <td className="text-center text-[9px]">{ot.estado}</td>
                    <td className="text-right font-mono">{fmt(ot.hh_presupuestadas)}</td>
                    <td className="text-right font-mono">{fmt(ot.hh_reales)}</td>
                    <td className={`text-right font-mono font-bold ${ot.diferencia > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {ot.diferencia > 0 ? '+' : ''}{fmt(ot.diferencia)}
                    </td>
                    <td className={`text-right font-bold ${ot.porcentaje_uso > 100 ? 'text-red-600' : ''}`}>
                      {fmt(ot.porcentaje_uso)}%
                    </td>
                  </tr>
                ))}
                {/* Fila de totales */}
                <tr className="bg-gray-200 font-bold">
                  <td colSpan="5" className="text-right">TOTALES:</td>
                  <td className="text-right font-mono">{fmt(getTotalesHHOT().presupuestadas)}</td>
                  <td className="text-right font-mono">{fmt(getTotalesHHOT().reales)}</td>
                  <td className={`text-right font-mono ${getTotalesHHOT().reales - getTotalesHHOT().presupuestadas > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {getTotalesHHOT().reales - getTotalesHHOT().presupuestadas > 0 ? '+' : ''}
                    {fmt(getTotalesHHOT().reales - getTotalesHHOT().presupuestadas)}
                  </td>
                  <td className="text-right">
                    {getTotalesHHOT().presupuestadas > 0 ? fmt((getTotalesHHOT().reales / getTotalesHHOT().presupuestadas) * 100) : 0}%
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Reporte HH por Proyecto */}
      {activeReport === 'hh-proyecto' && (
        <div>
          {/* Filtros en línea */}
          <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-100 border print:hidden">
            <span className="text-xs font-bold">Filtros:</span>
            <select value={filtroEstadoProyecto} onChange={(e) => setFiltroEstadoProyecto(e.target.value)} className={selectStyle}>
              <option value="">Estado: Todos</option>
              {ESTADOS_PROYECTO.filter(e => e).map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select value={filtroProyectoCodigo} onChange={(e) => setFiltroProyectoCodigo(e.target.value)} className={selectStyle + " max-w-[200px]"}>
              <option value="">Proyecto: Todos</option>
              {proyectosLista.map(p => <option key={p.codigo} value={p.codigo}>{p.codigo} - {p.descripcion?.substring(0, 20)}</option>)}
            </select>
            <Button onClick={handleBuscarHHProyecto} size="sm" className={btnStyle}><Search className="w-3 h-3 mr-1" />Buscar</Button>
            <Button onClick={limpiarFiltrosProyecto} size="sm" variant="outline" className="h-7 px-2 text-xs"><X className="w-3 h-3" /></Button>
            <span className="text-xs ml-auto">{dataHHProyecto.length} Proyectos | {getTotalesProyecto().ots} OTs</span>
          </div>

          {/* Título para impresión */}
          <div className="hidden print:block text-center mb-2">
            <h2 className="text-sm font-bold">INFORME HH POR PROYECTO</h2>
            <p className="text-xs">Estado: {filtroEstadoProyecto || 'Todos'}</p>
          </div>

          {loading ? (
            <p className="text-center py-4 text-xs">Cargando...</p>
          ) : dataHHProyecto.length === 0 ? (
            <p className="text-center py-4 text-xs text-gray-500">Sin datos</p>
          ) : (
            <div className="space-y-1">
              {dataHHProyecto.map((proy, idx) => (
                <div key={idx} className="border border-gray-400">
                  {/* Cabecera del proyecto - clickeable */}
                  <div 
                    className="flex items-center gap-2 p-1 bg-gray-200 cursor-pointer hover:bg-gray-300 text-xs print:cursor-default"
                    onClick={() => toggleProyecto(proy.proyecto_codigo)}
                  >
                    <span className="print:hidden">
                      {expandedProyectos[proy.proyecto_codigo] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </span>
                    <span className="font-mono font-bold">{proy.proyecto_codigo}</span>
                    <span className="truncate max-w-[200px]">{proy.proyecto_descripcion}</span>
                    <span className="ml-auto font-mono">
                      <span className="mr-4">{proy.cantidad_ots} OTs</span>
                      <span className="mr-4">Pres: {fmt(proy.total_hh_presupuestadas)}</span>
                      <span className="mr-4">Real: {fmt(proy.total_hh_reales)}</span>
                      <span className={proy.diferencia_total > 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>
                        Dif: {proy.diferencia_total > 0 ? '+' : ''}{fmt(proy.diferencia_total)} ({fmt(proy.porcentaje_uso)}%)
                      </span>
                    </span>
                  </div>
                  
                  {/* Detalle OTs - expandible en pantalla, siempre visible en impresión */}
                  <div className={`${expandedProyectos[proy.proyecto_codigo] ? '' : 'hidden'} print:block`}>
                    <table className="report-table bordered-table w-full">
                      <thead>
                        <tr>
                          <th>OT</th>
                          <th>DESCRIPCIÓN</th>
                          <th>ESTADO</th>
                          <th>ÁREA</th>
                          <th className="text-right">HH PRES.</th>
                          <th className="text-right">HH REAL</th>
                          <th className="text-right">DIF.</th>
                          <th className="text-right">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proy.ots.map((ot, otIdx) => (
                          <tr key={otIdx}>
                            <td className="font-mono text-center">{ot.numero_ot}</td>
                            <td className="max-w-[150px] truncate">{ot.descripcion}</td>
                            <td className="text-center text-[9px]">{ot.estado}</td>
                            <td className="text-[9px]">{ot.area}</td>
                            <td className="text-right font-mono">{fmt(ot.hh_presupuestadas)}</td>
                            <td className="text-right font-mono">{fmt(ot.hh_reales)}</td>
                            <td className={`text-right font-mono ${ot.diferencia > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {ot.diferencia > 0 ? '+' : ''}{fmt(ot.diferencia)}
                            </td>
                            <td className={`text-right ${ot.porcentaje_uso > 100 ? 'text-red-600' : ''}`}>
                              {fmt(ot.porcentaje_uso)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              
              {/* Totales generales */}
              <div className="border-2 border-gray-600 p-1 bg-gray-100 text-xs font-bold">
                <span>TOTALES: </span>
                <span className="font-mono ml-4">HH Presupuestadas: {fmt(getTotalesProyecto().presupuestadas)}</span>
                <span className="font-mono ml-4">HH Reales: {fmt(getTotalesProyecto().reales)}</span>
                <span className={`font-mono ml-4 ${getTotalesProyecto().reales - getTotalesProyecto().presupuestadas > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  Diferencia: {getTotalesProyecto().reales - getTotalesProyecto().presupuestadas > 0 ? '+' : ''}
                  {fmt(getTotalesProyecto().reales - getTotalesProyecto().presupuestadas)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
